'use client';

import { useEffect } from 'react';

import type { Place, TypesenseEventDocument } from '@/types';
import { IconCalendarEventFilled } from '@tabler/icons-react';
import { parseAsIsoDateTime, useQueryState } from 'nuqs';

import { useGlobal } from '@/lib/global-store';
import { useBookButtonAction } from '@/lib/hooks/use-book-button-click';
import { getBookButtonText } from '@/lib/utils/get-book-button-text';

import { Button } from '../button';
import { EventActions } from './event-actions';

export function EventPageActions({ instances, place }: { instances: TypesenseEventDocument[]; place: Place }) {
  const [date] = useQueryState('date', parseAsIsoDateTime);
  const { setBookEvent, setBookTab, openBook, setDmnConfig } = useGlobal();
  const event = instances.find((instance) => instance.dates === (date?.getTime() || 0) / 1000) || instances[0];

  const onClick = useBookButtonAction(event, place);

  // set the event and tab when the component mounts so that the navigation book button automatically opens the current event
  useEffect(() => {
    if (event) {
      if (event.tonic?.id && event.tonic?.active) {
        setBookEvent(event);
        setBookTab('tonic');
      } else {
        setBookEvent(event);
        setDmnConfig({
          venue: place.apps?.designMyNight?.id,
          allowedTypes: event.designMyNight?.id,
          date: date?.toISOString(),
        });
        setBookTab('design-my-night');
      }
    }

    return () => {
      setBookEvent(null);
    };
  }, [event, setBookEvent, setBookTab, openBook, setDmnConfig, place.apps?.designMyNight?.id, date]);

  if (!event) {
    return null;
  }

  const buttonText = getBookButtonText(event);

  return (
    <>
      <Button
        href={event.bookingUrl || undefined}
        onClick={onClick}
        className="w-full sm:w-auto"
        disabled={['Sold Out', 'Cancelled'].includes(buttonText)}
        target={event.bookingUrl ? '_blank' : undefined}
      >
        {buttonText}
        <IconCalendarEventFilled className="size-4" />
      </Button>

      <EventActions className="size-10 sm:size-10" event={event} />
    </>
  );
}
